// pages/PlanningPokerPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  Eye,
  Plus,
  CheckCircle2,
  Play,
  Loader2,
  X,
  AlertCircle
} from 'lucide-react';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';

const API_BASE = '/api';

interface Task {
  _id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in-progress' | 'review' | 'done' | 'blocked' | 'archived';
  estimatedDuration?: number;
  createdBy?: string | { _id: string; name: string; email: string };
}

interface VoteData {
  _id: string;
  title: string;
  type: 'time_estimation';
  linkedTaskId?: string;
  status: 'active' | 'closed' | 'cancelled';
  options: Array<{
    text: string;
    order: number;
    hours?: number;
  }>;
  votes: Array<{
    userId: { _id: string; name: string; email: string };
    selectedOptions: Array<{
      optionIndex: number;
      customHours?: number;
    }>;
    votedAt: string;
  }>;
  results: {
    totalVotes: number;
    averageHours?: number;
  };
  estimatedHours?: number;
  userHasVoted?: boolean;
  canVote?: { canVote: boolean; reason?: string };
}

const PlanningPokerPage: React.FC = () => {
  const { projectId, teamId } = useParams<{ projectId: string; teamId: string }>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => selectUser(state));

  const [tasks, setTasks] = useState<Task[]>([]);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);
  const [currentVote, setCurrentVote] = useState<VoteData | null>(null);
  const [selectedEstimation, setSelectedEstimation] = useState<number | null>(null);
  const [customHours, setCustomHours] = useState<string>('');
  const [showReveal, setShowReveal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  // Mode d'estimation : 'planning-poker' (valeurs standardisées) ou 'detailed' (options détaillées)
  const [estimationMode, setEstimationMode] = useState<'planning-poker' | 'detailed'>('planning-poker');

  // Valeurs Planning Poker classiques (en heures)
  const planningPokerValues = [
    { label: '1', hours: 1 },
    { label: '2', hours: 2 },
    { label: '3', hours: 3 },
    { label: '5', hours: 5 },
    { label: '8', hours: 8 },
    { label: '13', hours: 13 },
    { label: '21', hours: 21 }
  ];

  // Options détaillées (pour estimation précise)
  const detailedValues = [
    { label: '30 min', hours: 0.5 },
    { label: '1h', hours: 1 },
    { label: '2h', hours: 2 },
    { label: '4h', hours: 4 },
    { label: '8h', hours: 8 },
    { label: '16h', hours: 16 },
    { label: '24h', hours: 24 },
    { label: '40h', hours: 40 },
    { label: '80h', hours: 80 }
  ];

  // Valeurs actives selon le mode
  const activeValues = estimationMode === 'planning-poker' ? planningPokerValues : detailedValues;

  useEffect(() => {
    if (projectId && teamId) {
      fetchTasks();
    }
  }, [projectId, teamId]);

  const fetchTasks = async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/tasks/team/${teamId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const fetchedTasks = Array.isArray(res.data.data?.tasks) ? res.data.data.tasks : [];
      setTasks(fetchedTasks);
      
      // Trouver la première tâche non estimée ou la première tâche
      const unestimatedTask = fetchedTasks.find((t: Task) => !t.estimatedDuration || t.estimatedDuration === 0);
      if (unestimatedTask) {
        setCurrentTask(unestimatedTask);
        await fetchVoteForTask(unestimatedTask._id);
      } else if (fetchedTasks.length > 0) {
        setCurrentTask(fetchedTasks[0]);
        await fetchVoteForTask(fetchedTasks[0]._id);
      }
    } catch (err: any) {
      console.error('Erreur chargement tâches:', err);
      setError('Error loading tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchVoteForTask = async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/votes/task/${taskId}/time-estimation`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setCurrentVote(res.data.data);
        setShowReveal(res.data.data.status === 'closed' || res.data.data.resultsVisibility === 'realtime');
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Erreur récupération vote:', err);
      }
      setCurrentVote(null);
    }
  };

  const selectTask = async (task: Task) => {
    setCurrentTask(task);
    setSelectedEstimation(null);
    setCustomHours('');
    setShowReveal(false);
    await fetchVoteForTask(task._id);
  };

  const createVoteForTask = async (task: Task) => {
    try {
      setSubmitting(true);
      setError(null);
      const res = await axios.post(
        `${API_BASE}/votes/task/${task._id}/time-estimation`,
        { endDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        await fetchVoteForTask(task._id);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error creating the vote');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEstimation = async () => {
    if (!currentTask || !currentVote) return;
    
    if (selectedEstimation === null && !customHours) {
      setError('Please select an estimate');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const payload: any = {};
      if (customHours && customHours.trim()) {
        const hours = parseFloat(customHours);
        if (isNaN(hours) || hours < 0) {
          setError('Veuillez entrer un nombre d\'heures valide');
          setSubmitting(false);
          return;
        }
        payload.customHours = hours;
      } else if (selectedEstimation !== null) {
        // Trouver l'option correspondante dans le vote
        const selectedValue = activeValues[selectedEstimation];
        const optionIndex = currentVote.options.findIndex(
          (opt: any) => opt.hours === selectedValue.hours
        );
        if (optionIndex >= 0) {
          payload.selectedOptions = [{ optionIndex }];
        } else {
          // Si l'option n'existe pas, utiliser customHours
          payload.customHours = selectedValue.hours;
        }
      }

      await axios.post(
        `${API_BASE}/votes/${currentVote._id}/vote`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      await fetchVoteForTask(currentTask._id);
      setSelectedEstimation(null);
      setCustomHours('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la soumission de l\'estimation');
    } finally {
      setSubmitting(false);
    }
  };

  const revealVotes = () => {
    setShowReveal(true);
  };

  const applyEstimation = async () => {
    if (!currentTask || !currentVote) return;
    
    try {
      setSubmitting(true);
      setError(null);
      await axios.post(
        `${API_BASE}/votes/${currentVote._id}/apply-estimation`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      await fetchTasks();
      await fetchVoteForTask(currentTask._id);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'application de l\'estimation');
    } finally {
      setSubmitting(false);
    }
  };

  const createTask = async () => {
    if (!newTaskTitle.trim() || !projectId || !teamId) return;
    
    try {
      setSubmitting(true);
      setError(null);
      const res = await axios.post(
        `${API_BASE}/tasks`,
        {
          title: newTaskTitle.trim(),
          project: projectId,
          team: teamId,
          status: 'todo'
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        await fetchTasks();
        setShowAddTaskModal(false);
        setNewTaskTitle('');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error creating the task');
    } finally {
      setSubmitting(false);
    }
  };

  const completedTasks = tasks.filter(t => t.estimatedDuration && t.estimatedDuration > 0).length;
  const totalTasks = tasks.length;
  const progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Ma session Planning Poker</h1>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Current Task Section */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Current task</h2>
              {currentTask ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                  <p className="text-lg font-semibold text-gray-900">{currentTask.title}</p>
                </div>
              ) : (
                <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 mb-3">
                  <p className="text-gray-500">No task selected</p>
                </div>
              )}
              <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-600">Select your estimate below</p>
              </div>
            </div>

            {/* Estimation Selection */}
            {currentTask && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Choisissez votre estimation</h2>
                  {currentVote && (
                    <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => {
                          setEstimationMode('planning-poker');
                          setSelectedEstimation(null);
                          setCustomHours('');
                        }}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          estimationMode === 'planning-poker'
                            ? 'bg-white text-purple-700 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Planning Poker
                      </button>
                      <button
                        onClick={() => {
                          setEstimationMode('detailed');
                          setSelectedEstimation(null);
                          setCustomHours('');
                        }}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          estimationMode === 'detailed'
                            ? 'bg-white text-purple-700 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Detailed
                      </button>
                    </div>
                  )}
                </div>
                
                {!currentVote ? (
                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <p className="text-gray-600 mb-4">No estimation votes for this task</p>
                    <button
                      onClick={() => createVoteForTask(currentTask)}
                      disabled={submitting}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                    >
                      {submitting ? 'Creating...' : 'Create a vote'}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className={`grid gap-3 mb-6 ${
                      estimationMode === 'planning-poker' 
                        ? 'grid-cols-4' 
                        : 'grid-cols-3'
                    }`}>
                      {activeValues.map((value, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedEstimation(index);
                            setCustomHours('');
                          }}
                          disabled={currentVote.userHasVoted || submitting}
                          className={`bg-white border-2 rounded-lg p-4 text-center transition-all ${
                            selectedEstimation === index && !customHours
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-gray-200 hover:border-gray-300'
                          } ${currentVote.userHasVoted ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span className={`font-bold text-gray-900 ${
                            estimationMode === 'planning-poker' ? 'text-2xl' : 'text-lg'
                          }`}>{value.label}</span>
                        </button>
                      ))}
                    </div>

                    {currentVote.userHasVoted ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                        <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-2" />
                        <p className="text-green-700 font-medium">You already voted</p>
                      </div>
                    ) : (
                      <button
                        onClick={submitEstimation}
                        disabled={selectedEstimation === null && !customHours || submitting}
                        className="w-full bg-blue-600 text-white rounded-lg py-3 px-4 font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Envoi...
                          </>
                        ) : (
                          <>
                            <Eye className="h-5 w-5" />
                            Reveal votes
                          </>
                        )}
                      </button>
                    )}

                    {/* Reveal Results */}
                    {showReveal && currentVote && (currentVote.estimatedHours !== undefined && currentVote.estimatedHours !== null || currentVote.results?.averageHours !== undefined && currentVote.results?.averageHours !== null) && (
                      <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Results</h3>
                        <div className="text-center">
                          <p className="text-3xl font-bold text-blue-600 mb-2">
                            {currentVote.estimatedHours !== undefined && currentVote.estimatedHours !== null
                              ? currentVote.estimatedHours.toFixed(1)
                              : currentVote.results?.averageHours?.toFixed(1) || '0'} heures
                          </p>
                          <p className="text-gray-600">
                            ({Math.ceil((currentVote.estimatedHours || currentVote.results?.averageHours || 0) / 8)} jour(s))
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            {currentVote.results.totalVotes} vote{currentVote.results.totalVotes > 1 ? 's' : ''}
                          </p>
                          {(() => {
                            const taskCreatorId = typeof currentTask.createdBy === 'object'
                              ? currentTask.createdBy?._id
                              : currentTask.createdBy;
                            return taskCreatorId === user?._id && (
                              <button
                                onClick={applyEstimation}
                                disabled={submitting}
                                className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300"
                              >
                                {submitting ? 'Application...' : 'Appliquer l\'estimation'}
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Task List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Task list</h2>
                <button
                  onClick={() => setShowAddTaskModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  <Plus className="h-4 w-4" />
                  Ajouter
                </button>
              </div>
              <div className="space-y-2">
                {tasks.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-500">
                    No tasks
                  </div>
                ) : (
                  tasks.map((task) => {
                    const isCompleted = task.estimatedDuration && task.estimatedDuration > 0;
                    const isActive = currentTask?._id === task._id;
                    
                    return (
                      <div
                        key={task._id}
                        onClick={() => selectTask(task)}
                        className={`bg-white border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          isActive
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {isCompleted ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                          ) : isActive ? (
                            <Play className="h-5 w-5 text-blue-600 fill-blue-600 flex-shrink-0" />
                          ) : (
                            <div className="h-5 w-5 flex-shrink-0" />
                          )}
                          <span className={`flex-1 ${isActive ? 'font-semibold' : ''}`}>
                            {task.title}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Progress */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Progression</h2>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  {completedTasks}/{totalTasks}
                </p>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Task Modal */}
      {showAddTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Add a task</h3>
              <button
                onClick={() => {
                  setShowAddTaskModal(false);
                  setNewTaskTitle('');
                  setError(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={e => setNewTaskTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Task name"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      createTask();
                    }
                  }}
                />
              </div>
              <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                <button
                  onClick={createTask}
                  disabled={!newTaskTitle.trim() || submitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  {submitting ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={() => {
                    setShowAddTaskModal(false);
                    setNewTaskTitle('');
                    setError(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningPokerPage;

